import {
  Users,
  Mail,
  Shield,
  MoreVertical,
  UserPlus,
  Search,
} from 'lucide-react';
import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SupplierUserGroup } from '@/types/auth';

interface SupplierUser {
  id: string;
  name: string;
  email: string;
  role: SupplierUserGroup;
  status: 'active' | 'inactive' | 'pending';
  lastLogin: string | null;
  createdAt: string;
}

const mockUsers: SupplierUser[] = [
  {
    id: 'user-1',
    name: 'Robert Johnson',
    email: 'robert.johnson@sysco.com',
    role: SupplierUserGroup.SUPPLIER_ADMIN,
    status: 'active',
    lastLogin: '2026-01-30T08:30:00Z',
    createdAt: '2019-03-15T00:00:00Z',
  },
  {
    id: 'user-2',
    name: 'Maria Garcia',
    email: 'maria.garcia@sysco.com',
    role: SupplierUserGroup.SUPPLIER_SALES,
    status: 'active',
    lastLogin: '2026-01-29T14:22:00Z',
    createdAt: '2021-06-10T00:00:00Z',
  },
  {
    id: 'user-3',
    name: 'James Wilson',
    email: 'james.wilson@sysco.com',
    role: SupplierUserGroup.SUPPLIER_DELIVERY,
    status: 'active',
    lastLogin: '2026-01-30T06:15:00Z',
    createdAt: '2022-01-20T00:00:00Z',
  },
  {
    id: 'user-4',
    name: 'Sarah Chen',
    email: 'sarah.chen@sysco.com',
    role: SupplierUserGroup.SUPPLIER_SALES,
    status: 'pending',
    lastLogin: null,
    createdAt: '2026-01-28T00:00:00Z',
  },
  {
    id: 'user-5',
    name: 'Michael Brown',
    email: 'michael.brown@sysco.com',
    role: SupplierUserGroup.SUPPLIER_DELIVERY,
    status: 'inactive',
    lastLogin: '2025-12-15T10:00:00Z',
    createdAt: '2020-08-05T00:00:00Z',
  },
];

const roleLabels: Record<SupplierUserGroup, string> = {
  [SupplierUserGroup.SUPPLIER_ADMIN]: 'Administrator',
  [SupplierUserGroup.SUPPLIER_SALES]: 'Sales',
  [SupplierUserGroup.SUPPLIER_DELIVERY]: 'Delivery',
};

const roleColors: Record<SupplierUserGroup, string> = {
  [SupplierUserGroup.SUPPLIER_ADMIN]: 'bg-purple-100 text-purple-800',
  [SupplierUserGroup.SUPPLIER_SALES]: 'bg-blue-100 text-blue-800',
  [SupplierUserGroup.SUPPLIER_DELIVERY]: 'bg-green-100 text-green-800',
};

const statusColors: Record<SupplierUser['status'], string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
};

function formatLastLogin(lastLogin: string | null): string {
  if (!lastLogin) return 'Never';
  const date = new Date(lastLogin);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffHours < 48) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function CompanyUsersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredUsers = mockUsers.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeCount = mockUsers.filter((u) => u.status === 'active').length;
  const pendingCount = mockUsers.filter((u) => u.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-muted-foreground">Manage team members and their access</p>
        </div>
        <Button>
          <UserPlus className="h-4 w-4 mr-2" />
          Invite User
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-primary/10 p-3">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{mockUsers.length}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-green-100 p-3">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeCount}</p>
                <p className="text-sm text-muted-foreground">Active Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-yellow-100 p-3">
                <Mail className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingCount}</p>
                <p className="text-sm text-muted-foreground">Pending Invites</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Team Members</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-64 rounded-md border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="divide-y">
            {filteredUsers.map((user) => (
              <div key={user.id} className="flex items-center justify-between py-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted font-medium">
                    {user.name.split(' ').map((n) => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${roleColors[user.role]}`}>
                    {roleLabels[user.role]}
                  </span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${statusColors[user.status]}`}>
                    {user.status}
                  </span>
                  <span className="text-sm text-muted-foreground w-20 text-right">
                    {formatLastLogin(user.lastLogin)}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <Shield className="h-4 w-4 mr-2" />
                        Change Role
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Mail className="h-4 w-4 mr-2" />
                        Resend Invite
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive">
                        Deactivate User
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <div className="py-8 text-center text-muted-foreground">
                No users found matching your search.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
